import { Box, Divider, Stack } from '@mui/material'
import useOverlayLocalStorage from '@/hooks/useOverlayLocalStorage'
import { useGetDocumentCommentsCount } from '@/service-library/hooks/document-comments'
import { WorkflowState } from '@/types/workflows'
import DocumentApprovalsView from '@/components/approvals/DocumentApprovalsView'
import CommentsDrawer from '@/components/comments/CommentsDrawer'
import DrawerPortal from '@/components/drawer/DrawerPortal'
import useDocumentValidationSubscribe from '@/components/notifications/useDocumentValidationSubscribe'
import SplitDocumentView from '@/components/split-document/SplitDocumentView'
import ComparisonView from '@/components/validation/ComparisonView'
import { useDocumentChipsContext } from '@/components/validation/providers/DocumentChipsProvider'
import { useDocumentRowsContext } from '@/components/validation/providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import Validation from '@/components/validation/Validation'
import PurchaseOrderView from '../purchase-order-view/PurchaseOrderView'
import { ValidationOverlayContentContainerProps } from './ValidationOverlay'
import ValidationOverlayHeader from './ValidationOverlayHeader'

type ValidationOverlayContentProps = ValidationOverlayContentContainerProps & {
  documentView?: WorkflowState['default_document_view']
}

export default function ValidationOverlayContent({
  dataQueries,
  documentOverlay,
  documentsCount,
  documentIsInFlight,
  documentView,
  hasPreviousDocument,
  hasNextDocument,
  selectedIndex,
  goToPreviousDocument,
  goToNextAvailableDocumentOrClose,
  refetchTableData,
  setUseOnlySelected,
}: ValidationOverlayContentProps) {
  const documentQueryKey = dataQueries.documentQuery.queryKey
  const documentId = dataQueries.documentQuery.document?.id
  const { listQueryKey: chipsListQueryKey } = useDocumentChipsContext()
  const { queryKey: rowsListQueryKey } = useDocumentRowsContext()
  const { queryKey: rowValuesListQueryKey } = useDocumentRowValuesContext()

  // Comments panel shares the right-hand drawer slot with Field Details and is
  // mutually exclusive with it (see ValidationOverlayHeader).
  const commentsOverlay = useOverlayLocalStorage({
    localStorageKey: 'comments-drawer-is-open',
  })

  // A workflow state can have no view mode. Then no document body renders at
  // all, so there is nothing for comments to sit beside.
  const showComments = !!documentView

  // Single source of truth for the count badge: fetched once here and shared
  // with both the toolbar badge (DrawerToggleButtons) that displays it and the
  // comments drawer, whose create/delete mutations invalidate this exact query
  // key. Fetch failures leave `commentsCount` undefined, which just hides the
  // count — the toggle never depends on it.
  const {
    commentsCount,
    isPending: commentsCountIsPending,
    queryKey: commentsCountQueryKey,
  } = useGetDocumentCommentsCount({
    filters: { document_id: documentId, deleted_at__isnull: 'true' },
    enabled: !!documentId && showComments,
    placeholderData: (previousData) => previousData, // Keep the last known count while loading
  })

  useDocumentValidationSubscribe({
    documentId: dataQueries.documentQuery.document?.id,
    documentQueryKey,
    chipsListQueryKey,
    rowsListQueryKey,
    rowValuesListQueryKey,
  })

  return (
    <>
      <ValidationOverlayHeader
        documentView={documentView}
        documentQuery={dataQueries.documentQuery}
        commentsOverlay={commentsOverlay}
        commentsCount={commentsCount}
        commentsCountIsPending={commentsCountIsPending}
        onClose={() => {
          documentOverlay.closeDocument()
          refetchTableData()
          setUseOnlySelected?.(false)
        }}
        hasPreviousDocument={!documentIsInFlight && hasPreviousDocument}
        hasNextDocument={!documentIsInFlight && hasNextDocument}
        selectedIndex={selectedIndex}
        documentsCount={documentIsInFlight ? undefined : documentsCount}
        goToPreviousDocument={goToPreviousDocument}
        goToNextAvailableDocumentOrClose={goToNextAvailableDocumentOrClose}
      />
      <Divider />
      {showComments && (
        <CommentsDrawer
          overlay={commentsOverlay}
          documentId={documentId}
          countQueryKey={commentsCountQueryKey}
        />
      )}
      {documentView === 'validation' && (
        <Validation
          documentQuery={dataQueries.documentQuery}
          goToNextAvailableDocumentOrClose={goToNextAvailableDocumentOrClose}
        />
      )}
      {documentView && documentView !== 'validation' && (
        // A Drawer is invisible unless a matching DrawerPortal slot is on
        // screen. The validation view has its own slot inside Validation.
        // These four views share the one below, so don't remove it: without
        // it the comments drawer still mounts, but nobody can see it.
        <Stack
          direction="row"
          sx={{ flexGrow: 1, minHeight: 0, overflowX: 'hidden' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {documentView === 'comparison' && (
              <ComparisonView
                documentQuery={dataQueries.documentQuery}
                key={dataQueries.documentQuery.document?.id}
              />
            )}
            {documentView === 'split-document' &&
              dataQueries.documentQuery.document && (
                <SplitDocumentView
                  document={dataQueries.documentQuery.document}
                  documentQueryKey={dataQueries.documentQuery.queryKey}
                />
              )}
            {documentView === 'purchase-order' &&
              dataQueries.documentQuery.document && (
                <PurchaseOrderView
                  // The key is the document ID, so switching documents remounts
                  // this view. Without that, it would keep showing the old
                  // document's PO data while the new one is still loading.
                  key={dataQueries.documentQuery.document.id}
                  doc={dataQueries.documentQuery.document}
                />
              )}
            {documentView === 'approvals' &&
              dataQueries.documentQuery.document && (
                <DocumentApprovalsView
                  key={dataQueries.documentQuery.document.id}
                  document={dataQueries.documentQuery.document}
                />
              )}
          </Box>
          <DrawerPortal
            anchor="right"
            additionalId={documentView}
            sx={{ overflow: 'hidden' }}
          />
        </Stack>
      )}
    </>
  )
}
